import React, {useEffect, useRef, useState} from 'react';
import {Modal} from "./modal.jsx";
import {APIProvider, ControlPosition, Map, Marker} from "@vis.gl/react-google-maps";
import {GeocodeAddress} from "../utils/map-utils.jsx";
import Button from "../button/button.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../services/web/corporation-service.js";
import projectUtils from "../../utils/project-utils.js";
import {useLoaderData} from "react-router";
import locationService from "../../services/web/location-service.js";

const ModalReverseGeocoding = ({
    open,
    setOpen,
    onConfirm,
    address,
    setIsLoading = () => {},
    showConfirmLocationAndAddress = true
}) => {
    const loaderData = useLoaderData();

    const markerRef = useRef(undefined);
    const [addressModal, setAddressModal] = useState({open: false, address: {}});
    const [position, setPosition] = useState({lat: 0, lng: 0})
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

    const parsedLatitude = Number.parseFloat(address?.latitude);
    const parsedLongitude = Number.parseFloat(address?.longitude);
    const hasValidCoordinates = Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude);

    const {data: corporation} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    useEffect(() => {
        if (!open) {
            setPosition({lat: 0, lng: 0})
        }
    }, [open]);

    useEffect(() => {
        if (!corporation || !open) return;

        const fallbackLatitude = Number.parseFloat(corporation?.latitude) || 0;
        const fallbackLongitude = Number.parseFloat(corporation?.longitude) || 0;

        const initialPosition = {
            lat: hasValidCoordinates ? parsedLatitude : fallbackLatitude,
            lng: hasValidCoordinates ? parsedLongitude : fallbackLongitude
        };

        setPosition(initialPosition);
    }, [corporation, address?.latitude, address?.longitude, open]);

    useEffect(() => {
        if (!open || !markerRef.current) return;

        markerRef.current.setPosition(position);
    }, [open, position]);

    const submit = () => {
        if (isReverseGeocoding) return;

        setIsReverseGeocoding(true);
        setIsLoading(true);

        const latLng = markerRef.current.getPosition();

        const formData = new FormData();
        formData.append("latitude", latLng.lat());
        formData.append("longitude", latLng.lng());

        locationService.reverseGeocoding(formData)
            .then((data) => {
                if (data !== null && data !== undefined) {
                    setOpen(false);
                    setAddressModal({open: true, address: {...data, latitude: latLng.lat(), longitude: latLng.lng(), changeAddress: true}});
                } else {
                    projectUtils.swalErrorMessage("", "Nenhum endereço encontrado", "Ok");
                }
            })
            .catch(error => {
                projectUtils.swalErrorMessage("", "Não foi possível buscar a localização", "Ok");
            })
            .finally(() => {
                setIsReverseGeocoding(false);
                setIsLoading(false);
            });
    }

    const confirm = () => {
        onConfirm(addressModal.address);
        setAddressModal({open: false, address: {}});
    }

    const confirmLatLng = () => {
        const latLng = markerRef.current.getPosition();

        onConfirm({...address, latitude: latLng.lat(), longitude: latLng.lng()});
        setOpen(false);
    }

    const changeLocation = (latLng) => {
        setPosition(latLng);
    };

    const formatAddressValue = (value) => {
        if (Array.isArray(value)) {
            return value
                .map(item => item?.label ?? item?.text ?? item?.name ?? item?.value ?? item)
                .filter(Boolean)
                .join(", ") || "-";
        }

        if (value && typeof value === "object") {
            return value.label ?? value.text ?? value.name ?? value.value ?? "-";
        }

        return value || "-";
    };

    return (
        <>
            <Modal open={open} setOpen={setOpen} title={"Mapa"}>
                <Modal.Body className="!p-0 !m-0 align-self-center justify-items-center">
                    <APIProvider apiKey={corporation?.maps_api_key}>
                        <Map
                            reuseMaps={false}
                            defaultZoom={12}
                            streetViewControl={false}
                            defaultCenter={position}
                            gestureHandling={'greedy'}
                            style={{height: "calc(-250px + 100vh)", width: "100%"}}
                            mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                            fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                            onClick={(event) => changeLocation(event.detail.latLng)}
                        >
                            <GeocodeAddress
                                address={address}
                                changeLocation={changeLocation}
                                parsedLatitude={parsedLatitude}
                                parsedLongitude={parsedLongitude}
                                position={position}
                            />

                            <Marker
                                position={position}
                                onDragEnd={(event) => changeLocation(event.latLng)}
                                draggable={true}
                                ref={markerRef}
                            />
                        </Map>
                    </APIProvider>
                </Modal.Body>
                <Modal.Footer>
                    {
                        (!showConfirmLocationAndAddress ||
                            (address?.latitude !== null && address?.longitude !== null)) &&
                        <Button target="primary" onClick={confirmLatLng}>
                            Confirmar localização
                        </Button>
                    }
                    {
                        loaderData?.hasDispatch && showConfirmLocationAndAddress &&
                        <Button target="primary" onClick={submit} loading={isReverseGeocoding} loadingText="Buscando...">
                            Confirmar localização e endereço
                        </Button>
                    }
                </Modal.Footer>
            </Modal>

            <Modal open={addressModal.open} setOpen={() => setAddressModal({open: false, address: {}})}
                   title={"Deseja sobrescrever o endereço?"}>
                <Modal.Body className="align-self-center justify-items-center">
                    <table className="w-full border border-[#ebebeb] text-sm text-left text-gray-700">
                        <thead className="bg-[#f5f5f6]">
                        <tr className="divide-x divide-[#ebebeb]">
                            <th scope="col" className="w-1/5 px-3 py-2"></th>
                            <th scope="col" className="w-2/5 px-3 py-2 text-center">Endereço atual</th>
                            <th scope="col" className="w-2/5 px-3 py-2 text-center">Endereço novo</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-[#ebebeb]">
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Local pré-cadastrado</td>
                            <td className="px-3 py-2 text-center">{address?.place || "-"}</td>
                            <td className="px-3 py-2 text-center">-</td>
                        </tr>
                        {
                            address?.type &&
                            <tr className="divide-x divide-[#ebebeb]">
                                <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Tipo</td>
                                <td className="px-3 py-2 text-center">{address?.type || "-"}</td>
                                <td className="px-3 py-2 text-center">-</td>
                            </tr>
                        }
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Logradouro</td>
                            <td className="px-3 py-2 text-center">{address?.address || "-"}</td>
                            <td className="px-3 py-2 text-center">{addressModal?.address.street || "-"}</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Número</td>
                            <td className="px-3 py-2 text-center">{address?.number || "-"}</td>
                            <td className="px-3 py-2 text-center">{addressModal?.address.number || "-"}</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Bairro</td>
                            <td className="px-3 py-2 text-center">{address?.neighborhood || "-"}</td>
                            <td className="px-3 py-2 text-center">{addressModal?.address.neighborhood || "-"}</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Cruzamento</td>
                            <td className="px-3 py-2 text-center">{address?.crossroads || "-"}</td>
                            <td className="px-3 py-2 text-center">-</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Ponto de referência</td>
                            <td className="px-3 py-2 text-center">{address?.referencePoint || "-"}</td>
                            <td className="px-3 py-2 text-center">-</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Município</td>
                            <td className="px-3 py-2 text-center">{address?.city ? address.city.split(" - ")[0] : "-"}</td>
                            <td className="px-3 py-2 text-center">{addressModal?.address.city || "-"}</td>
                        </tr>
                        <tr className="divide-x divide-[#ebebeb]">
                            <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Estado</td>
                            <td className="px-3 py-2 text-center">{address?.city ? address.city.split(" - ")[1] : "-"}</td>
                            <td className="px-3 py-2 text-center">{addressModal?.address.district || "-"}</td>
                        </tr>
                        {
                            address && "sector" in address &&
                            <tr className="divide-x divide-[#ebebeb]">
                                <td scope="row" className="px-3 py-2 bg-[#fcfcfc]">Setor</td>
                                <td className="px-3 py-2 text-center">{formatAddressValue(address?.sector)}</td>
                                <td className="px-3 py-2 text-center">{formatAddressValue(addressModal?.address.sector)}</td>
                            </tr>
                        }
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer>
                    <Button target="primary" onClick={confirm}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ModalReverseGeocoding;
